/**
 * Shared starting-bid helpers (client-side).
 * Policy:
 *  - Compounded per-period rate: i = (1+R)^(1/F) - 1
 *  - Raw per-period starting bid: ((M-1)/M) * P * (1 - (1+i)^(p-M))
 *  - organiserPeriod = 0, last period (p = M) = 0
 *  - Rounding rule: ceil to bidStep
 *  - "Only last is 0": if raw > 0 and p < M, ensure at least one step after rounding
 *
 * @typedef {Object} BuildArgs
 * @property {number} duration
 * @property {number} contribution
 * @property {string} frequency - 'weekly' | 'fortnightly' | 'monthly'
 * @property {number} bidStep
 * @property {number} annualRate
 * @property {number} [organiserPeriod=0]
 * @property {'ceil'|'floor'|'nearest'} [rounding='ceil']
 */

/** @param {string} freq */
export function periodsPerYear(freq) {
  const f = String(freq || '').toLowerCase();
  if (f === 'weekly') return 52;
  if (f === 'fortnightly') return 26;
  return 12; // monthly default
}

/** @param {number} R @param {number} F */
export function normalizeAnnualRate(annualRate) {
  const n = Number(annualRate);
  if (!Number.isFinite(n)) return 0.10; // default 10%
  // If someone accidentally passes a percent (e.g. 10 for 10%), normalise it.
  if (n > 1 && n <= 100) return n / 100;
  return n;
}

function perPeriodRate(R, F) {
  const annual = normalizeAnnualRate(R);
  return Math.pow(1 + annual, 1 / F) - 1; // compounded
}

/** @param {number} x @param {number} step @param {'ceil'|'floor'|'nearest'} [mode='ceil'] */
export function roundToStep(x, step, mode = 'ceil') {
  const s = Number(step || 0);
  if (!s || s <= 0) return x;
  if (mode === 'floor')   return Math.floor(x / s) * s;
  if (mode === 'nearest') return Math.round(x / s) * s;
  return Math.ceil(x / s) * s; // default
}

/**
 * @param {BuildArgs} args
 * @returns {{ period:number, raw:number, recommended:number }[]}
 */
export function buildStartingBidSchedule({
  duration,
  contribution,
  frequency,
  bidStep,
  annualRate,
  organiserPeriod = 0,
  rounding = 'ceil',
}) {
  const M = Number(duration);
  const C = Number(contribution);
  const P = M * C;
  const F = periodsPerYear(frequency);
  const i = perPeriodRate(annualRate, F);
  const step = Number(bidStep || 100);
  const orgP = Number(organiserPeriod || 0);

  /** @type {{ period:number, raw:number, recommended:number }[]} */
  const rows = [];

  for (let p = 1; p <= M; p++) {
    const raw = (p === orgP)
      ? 0
      : ((M - 1) / M) * P * (1 - Math.pow(1 + i, p - M));

    // snap to policy step; ensure only last is 0
    let rec = roundToStep(raw, step, rounding);
    if (p < M && raw > 0 && rec === 0 && step > 0) rec = step;
    if (p === M || p === orgP) rec = 0;

    rows.push({
      period: p,
      raw: Number.isFinite(raw) ? raw : 0,
      recommended: Number.isFinite(rec) ? rec : 0,
    });
  }

  return rows;
}
